import _ from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Reports from "../../common/reports/Reports";
import Autocomplete from "../../framework/Autocomplete";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import classes from "./Profession.module.css";
import ProfessionMasterReportFilter from "./ProfessionMasterReportFilter";
import ProfessionMasterReportSummary from "./ProfessionMasterReportSummary";
import EditProfession from "./editProfession/index";

const Profession = () => {
  const [showEditProfession, setShowEditProfession] = useState(false);
  const [professionData, setProfessionData] = useState(null);
  const [onFilterShow, setOnFilterShow] = useState(false);
  const [onColumnShow, setOnColumnShow] = useState(false);
  const [rowLevelCTAs, setRowLevelCTAs] = useState([]);
  const [filterStates, setFilterStates] = useState(null);
  const [currentRowData, setCurrentRowData] = useState(null);
  const { setCurrentComponent, currentComponent } = useContext(GlobalContext);
  const reportRef = useRef();
  const editRef = useRef();
  const refreshRef = useRef();

  const refreshData = useCallback((filterData = {}) => {
    reportRef.current.applyFilters(filterData);
  }, []);

  // Renamed the callback function to avoid conflict
  const handleEditProfession = useCallback((isEdit = false) => {
    if (isEdit) {
      const professionData = reportRef.current.getCurrentRowData();
      setProfessionData(professionData);
    }
    setShowEditProfession(true);
  }, []);

  const closeEditProfessionAc = useCallback(() => {
    setShowEditProfession(false);
    setProfessionData(null);
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  const debounceCloseProfessionAc = _.debounce(closeEditProfessionAc, 200);

  const listener = useCallback(
    (e) => {
      const key = e.key?.toLowerCase();
      const ctrlKey = e.ctrlKey;
      const altKey = e.altKey;
      if (altKey && key === "r") {
        refreshRef.current.click();
        return;
      }
      if (ctrlKey && key === "e") {
        if (currentRowData)
          return editRef.current.click();
      }
    },
    [currentRowData]
  );

  useEffect(() => {
    const allRowCTAs = [
      <Button
        ref={editRef}
        startIcon={<Shortcut keys={["Ctrl", "E"]} />}
        label="Edit"
        onClick={() => {
          handleEditProfession(true);
        }}
        isAccessible={currentRowData}
      />,
    ];
    setRowLevelCTAs(allRowCTAs);
  }, [currentRowData, handleEditProfession]);

  useEffect(() => {
    if (
      !onFilterShow &&
      !showEditProfession &&
      !onColumnShow &&
      currentComponent === "grid_reports"
    ) {
      document.addEventListener("keyup", listener, false);
    }
    return () => {
      document.removeEventListener("keyup", listener, false);
    };
  }, [
    currentComponent,
    listener,
    onColumnShow,
    onFilterShow,
    showEditProfession,
  ]);

  useEffect(() => {
    refreshData({ isFilterChanged: true, ...filterStates });
  }, [refreshData, filterStates]);

  return (
    <div className={classes["profession-report-container"]}>
      <Reports
        identifier="professionMaster"
        reportRef={reportRef}
        rowLevelCTAs={rowLevelCTAs}
        onFilterShowClick={setOnFilterShow}
        onColumnShowClick={setOnColumnShow}
        filters={
          <ProfessionMasterReportFilter
            reportRef={reportRef}
            filters={(filters) => {
              setFilterStates(filters);
            }}
            onFilterShow={onFilterShow}
          />
        }
        currentRow={(data) => {
          setCurrentRowData(data);
        }}
      />
      <ProfessionMasterReportSummary
        refreshData={refreshData}
        refreshRef={refreshRef}
        showAutoComplete={() => {
          handleEditProfession();
        }}
      />
      <Autocomplete
        show={showEditProfession}
        height="50%"
        width="50%"
        closeAutoComplete={debounceCloseProfessionAc}
      >
        <EditProfession
          professionData={professionData}
          onEscape={debounceCloseProfessionAc}
          onSaveProfession={() => {
            debounceCloseProfessionAc();
            refreshData();
          }}
        />
      </Autocomplete>
    </div>
  );
};

export default Profession;
